import { InvoiceFormExportSection } from "./sections/InvoiceFormExportSection";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import React, { useEffect, useRef, useState } from "react";
import { Invoice } from "../../models/invoice";
import {
  ItemRequestStatus,
  createItem,
  getItem,
  updateItem,
} from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import {
  CButton,
  CCard,
  CCardBody,
  CCardFooter,
  CCardHeader,
} from "@coreui/react";
import { useHistory } from "react-router-dom";
import { errorAlert, successAlert } from "../utils/messages";
import Loading from "../indicators/Loading";
import ClientUpdateModal from "../client/ClientUpdateModal";
import { Spinner } from "react-bootstrap";
import { getRoutePath } from "../../routes";
import NotFound from "../indicators/NotFound";
import ServerError from "../indicators/ServerError";
import { getClientForDocument } from "../../api/client";
import { getStampById } from "../../api/stamp";
import { getEstablishmentById } from "../../api/establishment";
import { getDispatchPointById } from "../../api/dispatch-point";
import { changeTZToPyg, changeTZToUtc } from "../../utils/dates";
import Client from "../../models/client";
import { PYG } from "../../currency/available-currencies";
import { useIdempotencyKey } from "../../hooks/use-idempotency-key";
import { getUnlistedDescriptions } from "../../api/catalog-items";
import { CatalogSuggestion } from "../../models/catalog-item";
import CatalogItemNudgeModal, {
  dismissedCatalogNudges,
} from "../catalog-item/CatalogItemNudgeModal";

// Context provider
import {
  InvoiceFormProvider,
  useInvoiceFormContext,
} from "./context/InvoiceFormContext";

// Section components
import { InvoiceFormHeader } from "./sections/InvoiceFormHeader";
import { InvoiceFormDNCPSection } from "./sections/InvoiceFormDNCPSection";
import { InvoiceFormSaleSection } from "./sections/InvoiceFormSaleSection";
import { InvoiceFormTotals } from "./sections/InvoiceFormTotals";
import { InvoiceFormItems } from "./sections/InvoiceFormItems";

interface InvoiceFormProps {
  readonly?: boolean;
}

// Inner component that uses the context
const InvoiceFormContent: React.FC<{
  readonly?: boolean;
}> = ({ readonly }) => {
  const [showAddClient, setShowAddClient] = useState(false);
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints
  );
  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const submissionLockRef = useRef(false);
  const idempotencyKey = useIdempotencyKey();
  const [loadError, setLoadError] = useState(false);
  const [loadNotFound, setLoadNotFound] = useState(false);
  const [nudgeSuggestions, setNudgeSuggestions] = useState<CatalogSuggestion[]>(
    []
  );

  const history = useHistory();

  const {
    invoiceRef,
    setInvoiceDate,
    setStamp,
    setEstablishment,
    setDispatchPoint,
    setClient,
    setCurrency,
    setPygExchangeRate,
    onItemsChange,
    setErrors,
  } = useInvoiceFormContext();

  const getEditingInvoiceId = () => {
    const urlParams = new URLSearchParams(history.location.search);
    const editingInvoiceId = urlParams.get("id");

    if (editingInvoiceId !== null) {
      if (!isNaN(parseInt(editingInvoiceId))) {
        return parseInt(editingInvoiceId);
      }
    }

    return undefined;
  };

  // Auto-select stamp, establishment and dispatch point if only one available
  useEffect(() => {
    const editingInvoiceId = getEditingInvoiceId();
    if (editingInvoiceId !== undefined) {
      return;
    }

    if (stamps.length === 1 && invoiceRef.current?.stampId === undefined) {
      const stamp = stamps[0];
      if (invoiceRef.current) {
        invoiceRef.current.stampId = stamp.id;
        invoiceRef.current.stampIdentifier = stamp.identifier;
        invoiceRef.current.stampBeginDate = stamp.beginDate;
      }
      setStamp(stamp);
    }

    if (
      establishments.length === 1 &&
      invoiceRef.current?.establishmentId === undefined
    ) {
      const establishment = establishments[0];
      if (invoiceRef.current) {
        invoiceRef.current.establishmentId = establishment.id;
        invoiceRef.current.establishmentCode = establishment.code;
      }
      setEstablishment(establishment);
    }

    if (
      dispatchPoints.length === 1 &&
      invoiceRef.current?.dispatchPointId === undefined
    ) {
      const dispatchPoint = dispatchPoints[0];
      if (invoiceRef.current) {
        invoiceRef.current.dispatchPointId = dispatchPoint.id;
        invoiceRef.current.dispatchPointIdentifier = dispatchPoint.identifier;
      }
      setDispatchPoint(dispatchPoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamps, establishments, dispatchPoints]);

  const loadInitialInvoiceById = async (): Promise<Invoice | null> => {
    const editingInvoiceId = getEditingInvoiceId();

    if (editingInvoiceId === undefined) {
      return null;
    }

    let requestStatus = await getItem<Invoice>(
      `/invoices/${editingInvoiceId}/`
    );

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.status === NOT_FOUND) {
        setLoadNotFound(true);
      } else {
        setLoadError(true);
      }

      return null;
    }

    if (requestStatus.data === undefined || requestStatus.data === null) {
      setLoadError(true);
      return null;
    }

    return requestStatus.data!;
  };

  const loadInitialData = async () => {
    setLoading(true);
    const invoice = await loadInitialInvoiceById();

    if (invoice === null) {
      setLoading(false);
      return;
    }

    const stampPromise = getStampById(invoice.stampId, invoice.taxPayerId);
    const establishmentPromise = getEstablishmentById(
      invoice.establishmentId,
      invoice.taxPayerId
    );
    const dispatchPointPromise = getDispatchPointById(
      invoice.dispatchPointId,
      invoice.taxPayerId
    );
    const clientPromise = getClientForDocument(invoice);

    const stamp = await stampPromise;
    const establishment = await establishmentPromise;
    const dispatchPoint = await dispatchPointPromise;
    const client = await clientPromise;

    invoiceRef.current = invoice;

    // The date from server is in Paraguay timezone
    // Create a Date object interpreting the server date components as local time
    const pygDate = new Date(invoice.invoiceDate!);
    // Extract the date/time components from the ISO string (which represents PYG time)
    const year = pygDate.getUTCFullYear();
    const month = pygDate.getUTCMonth();
    const day = pygDate.getUTCDate();
    const hours = pygDate.getUTCHours();
    const minutes = pygDate.getUTCMinutes();
    const seconds = pygDate.getUTCSeconds();
    // Create a new Date treating these components as local time
    const invoiceDate = new Date(year, month, day, hours, minutes, seconds);
    setInvoiceDate(invoiceDate);
    setStamp(stamp);
    setEstablishment(establishment);
    setDispatchPoint(dispatchPoint);
    setClient(client);
    onItemsChange(invoice.items);

    setCurrency(invoice.currency ? invoice.currency : PYG);
    setPygExchangeRate(invoice.pygExchangeRate);

    setLoading(false);
  };

  useEffect(() => {
    const newInvoiceDate = changeTZToPyg(new Date());
    if (invoiceRef.current !== null) {
      invoiceRef.current.invoiceDate =
        changeTZToUtc(newInvoiceDate).toISOString();
      setInvoiceDate(newInvoiceDate);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddClientSuccess = (newClient?: Client) => {
    let message = "Cliente agregado correctamente";
    if (newClient) {
      message = `Cliente ${newClient.socialName} agregado correctamente`;
      if (invoiceRef.current) {
        invoiceRef.current.clientId = newClient.id;
        invoiceRef.current.clientCode = newClient.code;
        invoiceRef.current.clientIsTaxPayer = newClient.isTaxPayer;
        invoiceRef.current.clientRuc = newClient.ruc;
        invoiceRef.current.clientSocialName = newClient.socialName;
        invoiceRef.current.clientFantasyName = newClient.fantasyName;
        invoiceRef.current.clientType = newClient.type;
        invoiceRef.current.clientCountryCode = newClient.countryCode;
        invoiceRef.current.clientHasAddress = newClient.hasAddress;
        invoiceRef.current.clientAddress = newClient.address;
        invoiceRef.current.clientHouseNumber = newClient.houseNumber;
        invoiceRef.current.clientDepartment = newClient.department;
        invoiceRef.current.clientDepartmentDescription =
          newClient.departmentDescription;
        invoiceRef.current.clientDistrict = newClient.district;
        invoiceRef.current.clientDistrictDescription =
          newClient.districtDescription;
        invoiceRef.current.clientCity = newClient.city;
        invoiceRef.current.clientCityDescription = newClient.cityDescription;
        invoiceRef.current.clientPhoneNumber = newClient.phoneNumber;
        invoiceRef.current.clientEmail = newClient.email;
      }
      setClient(newClient);
    }
    successAlert(message);
    setShowAddClient(false);
  };

  const goToInvoices = () => {
    history.push(getRoutePath("invoices"));
  };

  /**
   * Offers to catalogue the lines of the document that just went out.
   *
   * Asked after the save and never before, so nothing here can keep an invoice
   * from being issued: a failed lookup just goes to the list, which is what
   * used to happen always. Only descriptions invoiced three times or more and
   * not already refused come back, so most saves go straight through.
   */
  const offerCatalogEntries = async () => {
    const descriptions = (invoiceRef.current?.items ?? [])
      .map((item) => (item.description ?? "").trim())
      .filter((description) => description !== "");

    if (descriptions.length === 0 || taxPayer.id === undefined) {
      goToInvoices();
      return;
    }

    const status = await getUnlistedDescriptions(taxPayer.id, descriptions);

    if (status.status !== SUCCESS || status.data === undefined) {
      goToInvoices();
      return;
    }

    const dismissed = dismissedCatalogNudges(taxPayer.id);
    const pending = status.data.suggestions.filter(
      (suggestion) =>
        dismissed.indexOf(suggestion.normalizedDescription) < 0
    );

    if (pending.length === 0) {
      goToInvoices();
      return;
    }

    setNudgeSuggestions(pending);
  };

  const onSave = async () => {
    if (invoiceRef.current === null || submissionLockRef.current) {
      return;
    }

    submissionLockRef.current = true;
    setSubmitting(true);

    let requestPromise: Promise<ItemRequestStatus<Invoice>>;
    let successMessage = "Factura agregada exitósamente";

    const invoice = invoiceRef.current;
    invoice.taxPayerRuc = taxPayer.ruc;
    invoice.taxPayerSocialName = taxPayer.socialName;
    invoice.taxPayerFantasyName = taxPayer.fantasyName;

    if (invoice.currency === undefined) {
      invoice.currency = PYG;
    }

    if (invoice.id === undefined) {
      requestPromise = createItem<Invoice>("/invoices/create/", invoice, {
        "Idempotency-Key": idempotencyKey,
      });
    } else {
      requestPromise = updateItem<Invoice>(`/invoices/${invoice.id}/`, invoice);
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }

      let message = "Ha ocurrido un error!!";

      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
      submissionLockRef.current = false;
    } else {
      successAlert(successMessage);
      await offerCatalogEntries();
    }
  };

  const onSaveDraft = async () => {
    if (invoiceRef.current === null || submissionLockRef.current) {
      return;
    }

    submissionLockRef.current = true;
    setSubmittingDraft(true);

    const invoice = invoiceRef.current;
    invoice.taxPayerRuc = taxPayer.ruc;
    invoice.taxPayerSocialName = taxPayer.socialName;
    invoice.taxPayerFantasyName = taxPayer.fantasyName;

    if (invoice.currency === undefined) {
      invoice.currency = PYG;
    }

    const requestStatus = await createItem<Invoice>("/invoices/create/", {
      ...invoice,
      isDraft: true,
    }, {
      "Idempotency-Key": idempotencyKey,
    });

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }
      let message = "Ha ocurrido un error!!";
      if (requestStatus.detail !== undefined) {
        message = requestStatus.detail;
      }
      errorAlert(message);
      setSubmittingDraft(false);
      submissionLockRef.current = false;
    } else {
      successAlert("Borrador guardado exitósamente");
      await offerCatalogEntries();
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (loadNotFound) {
    return <NotFound />;
  }

  if (loadError) {
    return <ServerError />;
  }

  return (
    <>
      <CCard className="shadow-sm border-0">
        <CCardHeader
          className="d-flex align-items-center"
          style={{
            background: "linear-gradient(180deg, #006657 0%, #095e55 100%)",
            color: "white",
            borderBottom: "none",
            padding: "14px 20px",
          }}
        >
          <i className="fa fa-file-text-o mr-2" style={{ fontSize: "1.1rem" }}></i>
          <span style={{ fontWeight: 600, fontSize: "1rem" }}>
            {invoiceRef.current !== null &&
            invoiceRef.current.id !== undefined ? (
              <>
                {readonly ? "Ver" : "Editar"} Factura Electrónica{" "}
                <span style={{ opacity: 0.85 }}>#{invoiceRef.current.id}</span>
              </>
            ) : (
              <>Agregar Factura Electrónica</>
            )}
          </span>
        </CCardHeader>
        <CCardBody style={{ padding: "24px" }}>
          <fieldset disabled={readonly}>
            <InvoiceFormHeader
              onAddClientClick={() => setShowAddClient(true)}
            />
            <InvoiceFormDNCPSection />
            <InvoiceFormSaleSection />
            <InvoiceFormExportSection />
            <InvoiceFormTotals />
            <InvoiceFormItems />
          </fieldset>
        </CCardBody>
        <CCardFooter
          style={{
            background: "var(--theme-surface-raised)",
            borderTop: "1px solid var(--theme-divider)",
            padding: "14px 20px",
          }}
        >
          {readonly !== true ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              <CButton
                type="submit"
                color="primary"
                onClick={onSave}
                disabled={submitting || submittingDraft}
                style={{ minWidth: "130px", fontWeight: 600, padding: "8px 20px" }}
              >
                {submitting ? (
                  <Spinner
                    animation="grow"
                    style={{
                      height: "17px",
                      width: "17px",
                      marginTop: "auto",
                      marginBottom: "auto",
                      marginRight: "10px",
                    }}
                  />
                ) : (
                  <i className="fa fa-save mr-2"></i>
                )}
                {submitting ? "Guardando..." : "Guardar"}
              </CButton>
              {invoiceRef.current?.id === undefined && (
                <CButton
                  type="button"
                  color="secondary"
                  onClick={onSaveDraft}
                  disabled={submitting || submittingDraft}
                  style={{ minWidth: "180px", fontWeight: 600, padding: "8px 20px" }}
                >
                  {submittingDraft ? (
                    <Spinner
                      animation="grow"
                      style={{
                        height: "17px",
                        width: "17px",
                        marginTop: "auto",
                        marginBottom: "auto",
                        marginRight: "10px",
                      }}
                    />
                  ) : (
                    <i className="fa fa-edit mr-2"></i>
                  )}
                  {submittingDraft ? "Guardando..." : "Guardar como borrador"}
                </CButton>
              )}
            </div>
          ) : (
            <></>
          )}
        </CCardFooter>
      </CCard>
      <ClientUpdateModal
        show={showAddClient}
        onSuccess={onAddClientSuccess}
        onCancel={() => setShowAddClient(false)}
      />
      <CatalogItemNudgeModal
        show={nudgeSuggestions.length > 0}
        suggestions={nudgeSuggestions}
        onClose={goToInvoices}
      />
    </>
  );
};

// Main wrapper component
const InvoiceForm: React.FC<InvoiceFormProps> = ({ readonly }) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  return (
    <InvoiceFormProvider taxPayerId={taxPayer.id} readonly={readonly}>
      <InvoiceFormContent readonly={readonly} />
    </InvoiceFormProvider>
  );
};

export default InvoiceForm;
